import { create } from "zustand";

export type StudioHistoryEntryType =
  | "document"
  | "slide"
  | "poll"
  | "canvas"
  | "settings";

export interface StudioHistoryScope {
  presentationId?: string | null;
  documentId?: string | null;
  slideId?: string | null;
  pollId?: string | null;
}

export interface StudioHistoryEntryMeta {
  [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface StudioHistoryEntry {
  id: string;
  timestamp: number;
  label: string;
  type: StudioHistoryEntryType;
  scope: StudioHistoryScope;
  meta?: StudioHistoryEntryMeta;
  undo: () => void | Promise<void>;
  redo: () => void | Promise<void>;
}

interface StudioHistoryState {
  entries: StudioHistoryEntry[];
  pointer: number; // -1 means initial state (no actions applied)
  maxLength: number;

  // When true, we are replaying history (undo/redo/jump) and
  // any new pushes should be ignored to avoid recursive entries.
  isReplaying: boolean;

  push: (entry: StudioHistoryEntry) => void;
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  jumpTo: (index: number) => Promise<void>;
  clear: () => void;
}

export const useStudioHistoryStore = create<StudioHistoryState>((set, get) => ({
  entries: [],
  pointer: -1,
  maxLength: 20,
  isReplaying: false,

  push: (entry) => {
    const { entries, pointer, maxLength, isReplaying } = get();

    // Do not record history while replaying existing entries
    if (isReplaying) return;

    // Drop all entries after the current pointer (standard undo semantics)
    const base = entries.slice(0, pointer + 1);
    let nextEntries = [...base, entry];

    // Enforce maxLength by trimming from the start
    if (nextEntries.length > maxLength) {
      const overflow = nextEntries.length - maxLength;
      nextEntries = nextEntries.slice(overflow);
    }

    set({
      entries: nextEntries,
      pointer: nextEntries.length - 1,
    });
  },

  undo: async () => {
    const { entries, pointer } = get();
    if (pointer < 0 || pointer >= entries.length) return;

    const entry = entries[pointer];
    set({ isReplaying: true });
    try {
      await entry.undo();
      set({ pointer: pointer - 1 });
    } finally {
      set({ isReplaying: false });
    }
  },

  redo: async () => {
    const { entries, pointer } = get();
    if (pointer >= entries.length - 1) return;

    const nextPointer = pointer + 1;
    const entry = entries[nextPointer];
    set({ isReplaying: true });
    try {
      await entry.redo();
      set({ pointer: nextPointer });
    } finally {
      set({ isReplaying: false });
    }
  },

  jumpTo: async (index: number) => {
    const { entries, pointer } = get();
    if (index === pointer) return;
    if (index < -1 || index >= entries.length) return;

    // Move stepwise using undo/redo semantics to keep side effects consistent
    set({ isReplaying: true });
    try {
      if (index < pointer) {
        // Need to undo backwards
        for (let i = pointer; i > index; i -= 1) {
          const entry = entries[i];
          // eslint-disable-next-line no-await-in-loop
          await entry.undo();
        }
      } else {
        // Need to redo forwards
        for (let i = pointer + 1; i <= index; i += 1) {
          const entry = entries[i];
          // eslint-disable-next-line no-await-in-loop
          await entry.redo();
        }
      }

      set({ pointer: index });
    } finally {
      set({ isReplaying: false });
    }
  },

  clear: () => set({ entries: [], pointer: -1, isReplaying: false }),
}));
